import { useLocalStorage } from '@vueuse/core'
import { Router } from 'src/router'
import { getAPIBaseURL, getStorageKey } from 'src/utils/utils'

import { api, checkAndRefreshTokens } from '../feathers-client'

const emptyMetadataResponse = {
  schema: {},
  filters: {},
  name: null,
  custom_actions: [],
}

let metadataFailureRedirectPending = false

const objectToQueryString = (obj) => {
  return Object.keys(obj)
    .filter((key) => obj[key] !== undefined)
    .map((key) => encodeURIComponent(key) + '=' + encodeURIComponent(obj[key]))
    .join('&')
}

const redirectToTechnicalIssue = () => {
  if (typeof window === 'undefined' || metadataFailureRedirectPending) {
    return
  }

  if (Router.currentRoute.value?.name === 'technical-issue') {
    return
  }

  metadataFailureRedirectPending = true
  Router.push({ name: 'technical-issue' })
    .catch(() => {})
    .finally(() => {
      metadataFailureRedirectPending = false
    })
}

export const useFeathers = () => {
  api.metadata = async (service, customMethod = null, queryStringParameters = {}) => {
    const accessToken = useLocalStorage(getStorageKey('access'), '')
    const refreshToken = useLocalStorage(getStorageKey('refresh'), '')

    if (accessToken.value) {
      const responseJson = await checkAndRefreshTokens(accessToken.value)

      if (responseJson === false) {
        return emptyMetadataResponse
      }

      if (responseJson !== true && responseJson?.accessToken) {
        accessToken.value = responseJson.accessToken
      }
      if (responseJson !== true && responseJson?.refreshToken) {
        refreshToken.value = responseJson.refreshToken
      }

      let url = getAPIBaseURL() + '/' + service

      if (Object.keys(queryStringParameters).length > 0) {
        const queryString = objectToQueryString(queryStringParameters)
        url += `?${queryString}`
      }
      const responseData = await fetch(url, {
        method: 'OPTIONS',
        headers: {
          Authorization: `Bearer ${accessToken.value}`,
          'Content-Type': 'application/json',
        },
      })
        .then((response) => {
          if (response.ok) {
            return response.json()
          } else {
            throw new Error('Network response was not ok.')
          }
        })
        .then((data) => {
          return data
        })
        .catch((error) => {
          redirectToTechnicalIssue()
          return false
        })

      if (responseData) {
        if (customMethod) {
          const customMethodData = responseData?.custom_actions.find((customAction) => customAction.name === customMethod)
          if (customMethodData) {
            return { schema: customMethodData.fields }
          }
          return { schema: {} }
        }

        return {
          schema: responseData.actions?.POST || {},
          filters: responseData.filters,
          name: responseData.name,
          custom_actions: responseData.custom_actions || [],
        }
      }
    }
    return emptyMetadataResponse
  }

  api.formdata = async (url, formData) => {
    const accessToken = useLocalStorage(getStorageKey('access'), '')
    const refreshToken = useLocalStorage(getStorageKey('refresh'), '')

    if (accessToken.value) {
      const responseJson = await checkAndRefreshTokens(accessToken.value)

      if (responseJson === false) {
        return {}
      }

      if (responseJson !== true && responseJson?.accessToken) {
        accessToken.value = responseJson.accessToken
      }
      if (responseJson !== true && responseJson?.refreshToken) {
        refreshToken.value = responseJson.refreshToken
      }

      if (!url.startsWith('/')) {
        url = '/' + url
      }
      if (!url.startsWith('http://') && !url.startsWith('https://')) {
        url = getAPIBaseURL() + url
      }

      return await fetch(url, {
        method: 'POST',
        body: formData,
        headers: {
          Authorization: `Bearer ${accessToken.value}`,
        },
      })
        .then((response) => {
          if (response.ok) {
            return response.json()
          } else {
            throw new Error('Network response was not ok.')
          }
        })
        .then((data) => {
          return data
        })
    }
    return false
  }
  return api
}

/**
 * Returns a service to work with Feathers-Pinia. It currently does not handle custom methods.
 * @param servicePath the path of the service
 */
export const useFeathersService = (servicePath, clientAlias = 'api') => {
  const clients = useFeathers()
  const client = clients[clientAlias]
  return client.service(servicePath)
}
